import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentTranscriptTextV1,
  DocumentTranscriptUtteranceV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import {
  createEditor,
  Descendant,
  Editor,
  NodeEntry,
  Text,
  Transforms
} from 'slate';
import { Editable, RenderElementProps, Slate, withReact } from 'slate-react';
import { HistoryEditor, withHistory } from 'slate-history';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import '@assets/css/transcript.css';
import { ParagraphLeaf } from '@components/editors/transcript-editor/body/paragraph-leaf.tsx';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { ParagraphElement } from '@components/editors/transcript-editor/body/paragraph-element.tsx';
import { TranscriptEditorLevelHotkeyProvider } from '@utils/providers/transcript-hotkey-editor-context.tsx';
import { useEditorContext } from '@utils/providers/editor-context.tsx';
import {
  createSafeSlateClickGuard,
  isAtStartOfNode,
  withErrorHandling
} from '@utils/extensions/slate-editor-utils';

type Props = {
  index: number;
  paragraph: DocumentTranscriptUtteranceV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  /**
   * Lock the Slate surface — no typing, no history mutations, no comment
   * compose. Used by the split-view source pane so the original stays
   * intact while the translator edits the right side. Selection, caret,
   * and click-to-activate still work so scroll sync is unaffected.
   */
  readOnly?: boolean;
};

const ScriptixTranscriptEditor = ({
  index,
  paragraph,
  onWordOutOfView,
  onWordInView,
  readOnly
}: Props) => {
  const { t } = useTranslation();
  const [editor] = useState(() =>
    withErrorHandling(withHistory(withReact(createEditor())))
  );

  // Tracks whether the last user gesture immediately preceding a focus event
  // was a pointer press (mouse/trackpad/touch). Used by onFocus to skip the
  // WCAG seed-selection when focus arrived via click — otherwise the seed
  // hijacks the click's caret target and, when the row was just remounted by
  // Virtuoso, its scrollSelectionIntoView jumps the view to the paragraph
  // start (Ingrid/Martijn trackpad regression).
  const lastPointerDownRef = useRef<number>(0);

  const {
    search,
    caseSensitive,
    wholeWord,
    currentMatch,
    document,
    setEditor,
    editorUpdateParagraph
  } = useTranscript();
  const { addEditor, removeEditor, trackModification } = useEditorContext();
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  useEffect(() => {
    addEditor(index.toString(), editor);

    return () => {
      removeEditor(index.toString());
    };
  }, [index, editor]);

  const initialValue = useMemo(() => {
    if (!paragraph || ('children' in paragraph && !paragraph['children']))
      return [];

    // Destructure `text` and `children` out so they don't leak onto element nodes.
    // Slate treats any node with a string `text` as a Text leaf, which breaks
    // element traversal when translated documents include `text` on utterances.
    const {
      text: utteranceText,
      children: utteranceChildren,
      ...paragraphBase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } = paragraph as any;

    const initialValues: ScriptixDescendant[] = [];
    initialValues.push({
      ...paragraphBase,
      id: index,
      type: 'speaker',
      children: [
        {
          text: paragraph.speaker || ''
        }
      ]
    } as ScriptixDescendant);

    initialValues.push({
      ...paragraphBase,
      id: index,
      type: 'paragraph',
      children: utteranceChildren?.length
        ? utteranceChildren
        : [{ text: utteranceText || '' }]
    } as ScriptixDescendant);

    return initialValues;
  }, [document]);

  // Decorate function for highlighting search matches
  const decorate = useCallback(
    (entry: NodeEntry) => {
      const [node, path] = entry;
      const ranges = [];

      if (search && Text.isText(node)) {
        const { text } = node;
        const flags = caseSensitive ? 'g' : 'gi';
        const pattern = wholeWord
          ? `\\b${escapeRegExp(search)}\\b`
          : escapeRegExp(search);
        const regex = new RegExp(pattern, flags);
        let match;

        while ((match = regex.exec(text)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          const isCurrent = currentMatch
            ? currentMatch.start === (node as DocumentTranscriptTextV1).start &&
              currentMatch.stop === (node as DocumentTranscriptTextV1).stop
            : false;

          ranges.push({
            anchor: { path, offset: start },
            focus: { path, offset: end },
            highlight: true,
            isCurrentMatch: isCurrent
          });
        }
      }

      return ranges;
    },
    [search, caseSensitive, wholeWord, currentMatch]
  );

  const handleChange = (children: Descendant[]) => {
    // Skip if this onChange was triggered only by a selection change (focus, click,
    // cursor move). Slate fires onChange for every set_selection operation too, and
    // deep-equality comparison against the stored document can produce false positives
    // due to Slate normalization stripping undefined fields. Only process when a
    // real content operation (insert_text, remove_text, set_node, etc.) was applied.
    const hasContentOp = editor.operations.some(
      op => op.type !== 'set_selection'
    );
    if (hasContentOp) {
      editorUpdateParagraph(children);
    }
    trackModification(editor);
  };

  const renderElement = useCallback((props: RenderElementProps) => {
    return (
      <ParagraphElement
        index={index}
        {...props}
        onWordOutOfView={onWordOutOfView}
        onWordInView={onWordInView}
        editor={editor}
      />
    );
  }, []);

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return <ParagraphLeaf {...props} />;
  }, []);

  return (
    <TranscriptEditorLevelHotkeyProvider editor={editor}>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onChange={handleChange}
      >
        <Editable
          readOnly={readOnly}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          // Do NOT autoFocus: Virtuoso mounts many rows synchronously, and every
          // Editable's autoFocus fires on mount — the last one wins and the
          // browser scrolls that contenteditable into view, jumping the editor
          // to the end of the visible list on open.
          // WCAG 2029: force the editable region into the tab sequence.
          // Slate relies on the browser's implicit tabindex for contenteditable
          // elements, which is inconsistent when the region contains focusable
          // children (split/merge/add/delete inline buttons) — some browsers
          // skip straight to those buttons and never land on the text.
          // In split-view read-only mode the pane is visual-only, so drop it
          // out of the tab sequence entirely.
          tabIndex={readOnly ? -1 : 0}
          aria-label={t('transcript-editor')}
          aria-multiline="true"
          aria-readonly={readOnly || undefined}
          onDragStart={e => e.preventDefault()}
          onDrop={e => e.preventDefault()}
          onClick={createSafeSlateClickGuard(editor)}
          style={{
            outline: 'unset',
            padding: '0em 0.5em',
            border: 'unset',
            width: '100%'
          }}
          className="no-outline"
          onKeyDown={event => {
            if (readOnly) return;
            if (event.ctrlKey) {
              switch (event.key) {
                case 'y':
                  event.preventDefault();
                  HistoryEditor.redo(editor as unknown as HistoryEditor);
                  break;
                // Ctrl/Cmd +, = and - stay with the browser (page zoom).
                case 'f':
                case 'left':
                case 'right':
                case 'b':
                case 'i':
                case 'u':
                case 's':
                  event.preventDefault();
                  break;
              }
            } else if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
              // If we're at the start of a node, prevent moving left due to moving to speaker crashing error
              if (isAtStartOfNode(editor)) {
                event.preventDefault();
              }
            }
          }}
          decorate={decorate}
          onPointerDown={() => {
            lastPointerDownRef.current = Date.now();
          }}
          onFocus={() => {
            if (readOnly) return;
            setEditor(editor);
            // WCAG #2029: seed a caret at the paragraph start (path [1]) when
            // Slate arrives with no selection so keyboard/Tab focus is
            // visible. Skip the seed when focus was pointer-initiated —
            // Slate's own click handler will place the caret where the user
            // clicked. Without this guard, mouse/trackpad clicks on a row
            // that Virtuoso just remounted (selection === null) get their
            // caret hijacked to the paragraph start, and Slate's
            // scrollSelectionIntoView jumps the view to that start (the
            // Ingrid/Martijn trackpad regression).
            const pointerJustFired =
              Date.now() - lastPointerDownRef.current < 300;
            if (!editor.selection && !pointerJustFired) {
              try {
                const paragraphPath = [1];
                if (Editor.hasPath(editor, paragraphPath)) {
                  Transforms.select(
                    editor,
                    Editor.start(editor, paragraphPath)
                  );
                } else {
                  Transforms.select(editor, Editor.start(editor, []));
                }
              } catch {
                // Node may not exist yet on the very first focus tick.
              }
            }
          }}
        />
      </Slate>
    </TranscriptEditorLevelHotkeyProvider>
  );
};

export default ScriptixTranscriptEditor;
